import {
  ContentInfo,
  CourseInfo,
  EntityInfo,
  EocClientStudyPlanMeta,
  StudentStudyPlanData,
  StudyPlanProgressModel,
  StudyPlanSubType,
  TestTimingSettingType
} from "../../../../types/gqlTypes/graphql"
import {
  EocClientDraftStudyPlan,
  ExistingPlanOptions,
  Program,
  EocClientStudyPlan,
  StudyPlanScope,
  EntityPlanInfo,
  RefScope,
  AvailableContentItem,
  NewPlanIntent
} from "../../types"
import { TimingOptionsValue } from "../../study-plans/components/TimingOptions/types"
import { User } from "../../../mp-database"
import { FiltersState } from "../../../reporting/states"

export enum StudyPlanTargets {
  ReviewPlan = "review_plan",
  Overview = "study_plans_overview",
  Landing = "landing_view",
  Edit = "edit_plan",
  TeacherView = "teacher_view",
  Settings = "global_settings",
  ViewRecommended = "view_recommended",
  TeacherOverall = "teacher_overall",
  Progress = "progress",
  ProgressByContent = "progress_by_content",
  ManageStudyPlans = "manage_study_plans",
  ProgressByStudent = "progress_by_student"
}

export type PlanSettings = {
  studentCanDoOutOfOrder?: boolean
  studentCanPauseTests?: boolean
  studentCanResetMasteryLessons?: boolean
  studentTestTimingValue?: number
  studentTestTimingType?: TestTimingSettingType
  timingOption?: TimingOptionsValue
}

export type ApiResponse = {
  response: any
  event: Transitions
}

export type LoadableSchema = {
  states: {
    loading: {}
    idle: {}
  }
}

export type PlansOverviewSchema = {
  states: {
    loading: {}
    idle: {}
    get_global_settings: {}
    save_global_plan_settings: {}
    get_admin_relevant_plan_info_transition: {}
  }
}

export type ScoresSchema = {
  states: {
    idle: {}
    loading: {}
    download_scores_by_student: {}
  }
}

export type AddLessonSchema = {
  states: {
    loading: {}
    idle: {}
    get_lessons_transition: {}
    get_plan_available_items_transition: {}
    undo_transition: {}
  }
}

export type ReviewPlanSchema = {
  states: {
    loading: {}
    idle: {}
    add_lesson: AddLessonSchema
    lesson_detail_view: LessonDetailsViewSchema
    restore_to_recommended_confirmation: {}
  }
}

export type PlanSettingsSchema = {
  states: {
    // NOTE: will use after modifications of getPlanHistory
    //loading: {}
    save_draft_plan_settings: {}
    undo: {}
    idle: {}
  }
}

export type TeacherViewSchema = {
  states: {
    loading: {}
    idle: {}
    add_lesson: AddLessonSchema
    lesson_detail_view: LessonDetailsViewSchema
    plan_settings: PlanSettingsSchema
    assign_class: LoadableSchema
    toggle_break_before_section_transition: {}
    toggle_break_after_section_transition: {}
    toggle_break_before_lesson_transition: {}
    toggle_break_after_lesson_transition: {}
    toggle_published_teach_live_transition: {}
    download_resources_transition: {}
    reset_mastery_lesson_transition: {}
    restore_mastery_lesson_transition: {}
    reset_mastery_lesson_confirmation: ConfirmResetRestoerMasteryLessonSchema
    restore_mastery_lesson_confirmation: ConfirmResetRestoerMasteryLessonSchema
    restore_to_recommended_confirmation: {}
    assign_subject_to_classes_transition: {}
    resolve_student_plan_conflict: ResolveStudentPlanConflictSchema
    get_teacher_assigned_subjects_transition: {}
    get_teacher_relevant_plan_info_transition: {}
  }
}

export type ConfirmResetRestoerMasteryLessonSchema = {
  states: {
    loading: {}
    idle: {}
  }
}

export type LessonDetailsViewSchema = {
  states: {
    get_lesson_details_transition: {}
    toggle_teach_live_transition: {}
    toggle_published_teach_live_transition: {}
    toggle_mark_complete_transition: {}
    toggle_published_mark_complete_transtion: {}
    download_resources_transition: {}
    change_micro_snaps_count_transition: {}
    idle: {}
  }
}

export type ResolveStudentPlanConflictSchema = {
  states: {
    resolve_conflict_transition: {}
    idle: {}
  }
}

export type ModifySectionSecondsSchema = {
  states: {
    idle: {}
    update_section_seconds_transition: {}
  }
}

export type ReorderModeSchema = {
  states: {
    idle: {}
    move_to_new_section_modal: {}
    reorder_section_transition: {}
    reorder_section_item_transition: {}
    move_to_new_section_transition: {}
  }
}

export type ViewBreakSchema = {
  states: {
    idle: {}
  }
}

export type EditPlanSchema = {
  states: {
    loading: {}
    /* Transitional states */
    remove_section_transition: {}
    toggle_break_before_section_transition: {}
    toggle_break_after_section_transition: {}
    toggle_break_before_lesson_transition: {}
    toggle_break_after_lesson_transition: {}
    convert_to_adaptive: {}
    convert_to_manual: {}
    /* Modals */
    confirm_first_saving: {}
    confirm_converting_plan: {}
    confirm_leave: {}
    /* Regular states */
    idle: {}
    go_back_to_previous: {}
    add_lesson: AddLessonSchema
    modify_section_seconds: ModifySectionSecondsSchema
    lesson_detail_view: LessonDetailsViewSchema
    plan_settings: PlanSettingsSchema
    reorder_mode: ReorderModeSchema
  }
}

// Study Plan V2 UI states
export type StudyPlanV2SettingsSchema = {
  states: {
    idle: {}
    loading: {}
    save_global_plan_settings: {}
  }
}

export type StudyPlanV2AdminManageStudyPlansSchema = {
  states: {
    idle: {}
    loading: {}
    get_current_plan: {}
    get_recommended_plan_info_transition: {}
    add_lesson: AddLessonSchema
    lesson_detail_view: LessonDetailsViewSchema
  }
}

export type StudyPlanV2ProgressByContentSchema = {
  states: {
    idle: {}
    loading: {}
    get_progress_by_content_progress: {}
  }
}

export type StudyPlanV2CheckMultipleFiltersTargetSchema = {
  states: {
    idle: {}
  }
}

export type StudyPlanV2Schema = {
  states: {
    idle: {}
    loading: {}
    check_multiple_filters_target: StudyPlanV2CheckMultipleFiltersTargetSchema
    sp_v2_progress_state: LoadableSchema
    sp_v2_settings_state: StudyPlanV2SettingsSchema
    sp_v2_progress_by_content_state: StudyPlanV2ProgressByContentSchema
    sp_v2_admin_manage_study_plans_state: StudyPlanV2AdminManageStudyPlansSchema
    sp_v2_progress_by_student_state: ScoresSchema
    sp_v2_teacher_overall_state: LoadableSchema
  }
}
// ------

export type Schema = {
  states: {
    /* Transitional states */
    build_populated_study_plan_transition: {}
    build_study_plan_transition: {}
    edit_published_plan_transition: {}
    fetch_study_plan_transition: {}
    get_current_plan: {}
    cancel_plan_transition: {}
    save_study_plan_transition: {}
    get_recommended_plan_info_transition: {}
    restore_to_recommended_plan_transition: {}
    undo_transition: {}
    redo_transition: {}
    remove_lesson_transition: {}
    replace_lesson_transition: {}
    add_lesson_transition: {}
    add_section_transition: {}
    get_lesson_preview_transition: {}
    /* Regular states */
    before_login: {}
    get_program_courses: LoadableSchema
    landing_view: LoadableSchema
    teacher_view: TeacherViewSchema
    study_plans_overview: PlansOverviewSchema
    start_plan: LoadableSchema
    review_plan: ReviewPlanSchema
    edit_plan: EditPlanSchema
    confirm_override: {}
    view_break: ViewBreakSchema
    //saved_plan_successfully: {}

    /* Study Plan V2 */
    study_plan_v2: StudyPlanV2Schema
  }
}

export type Transitions =
  | { type: "SYNC"; target: string }
  | { type: "SET_PLAN_REF_SCOPE"; scope: StudyPlanScope; targetRefId: string }
  | { type: "SET_HAS_UNSAVED_CHANGES"; value: boolean }
  | {
      type: "SET_STATE"
      program?: string
      subject?: string
      subpage?: string
      entityType?: string
      scope?: StudyPlanScope
      classId?: string
      className?: string
    }
  | { type: "CLEAR_REDIRECT_URL" }
  | { type: "CLEAR_EXTERNAL_REDIRECT_URL" }
  | { type: "CLOSE" }
  | { type: "CANCEL" }
  | { type: "LEAVE"; location?: string }
  | { type: "SAVE"; existingPlanOption?: ExistingPlanOptions }
  | { type: "CONFIRM" }
  | { type: "CHANGE_PROGRAM"; program: Program; subject?: string }
  | { type: "" } // If there is no action type, it means that the xState triggered a transition state without the user's action

  /* before_login */
  | {
      type: "SELECT_USER"
      newRole: StudyPlanScope
      newDistrictId: string
      newSchoolId?: string
      newTeacherId?: string
      newClassId?: string
      newStudentId?: string
    }

  /* landing_view */
  | { type: "MANAGE"; subject: string }
  | { type: "REPORTS" }
  | { type: "DISMISS_LEARN_MORE" }

  /* study_plans_overview events */
  | { type: "SELECT_SUBJECT"; newSubject: string }
  | { type: "GET_GLOBAL_SETTINGS" }
  | { type: "CREATE" }
  | { type: "EDIT"; selectedPlanId?: string } // Only need selectedPlan if not on review page of newly created plan
  | { type: "SUBJECT_CLASS_ASSIGNMENT" }
  | {
      type: "FETCH_PUBLISHED_PLAN"
      selectedPlanId: string
      entityId?: string
      isStudent?: boolean
    }
  | {
      type: "FETCH_PLAN_BY_REF_SCOPE"
      entityId?: string
      scope?: StudyPlanScope
      subject?: string
    }

  /* start_plan events */
  | {
      type: "seconds.update"
      seconds: number
    }
  | {
      type: "BUILD_STUDY_PLAN"
      intent?: NewPlanIntent
      subType?: StudyPlanSubType
    }
  | {
      type: "START_BLANK_STUDY_PLAN"
      subType?: StudyPlanSubType // subType for creating a MANUAL or ADAPTIVE new plan
    }

  /* review_plan events */
  | { type: "PLAN_SECOND_MODIFIER"; initSeconds: number }
  | { type: "VIEW_RECOMMENDED_PLAN" }
  /* review_plan teacher events */
  | { type: "ADD_CLASS_PLAN" }
  | { type: "REMOVE_BREAK" }
  | { type: "RESOLVE_STUDENT_CONFLICTS"; classId: string }
  | { type: "plan.checked"; classId: string }
  | { type: "REPORTS" }
  | { type: "INHERITED_PLAN" }
  | { type: "RESTORE_PLAN" }
  | { type: "ASSIGN_SUBJECT_TO_CLASSES"; ids: string[] }
  | { type: "DISMISS_DEFAULT_INHERITANCE" }
  | { type: "DISMISS_MASTER_PLAN_INFO" }
  | { type: "RESET_MASTERY_LESSON"; contentId?: string; allowUndo?: boolean }
  | { type: "RESTORE_MASTERY_LESSON"; contentId?: string; allowUndo?: boolean }
  | {
      type: "OPEN_RESET_MASTERY_LESSON_CONFIRMATION"
      contentId: string
      sectionId: string
    }
  | {
      type: "OPEN_RESTORE_MASTERY_LESSON_CONFIRMATION"
      contentId: string
      sectionId: string
    }
  /* review_plan adaptive plan only */
  | { type: "TOGGLE_ADAPTIVE" }
  | {
      type: "VIEW_DETAILS"
      sectionId?: string
      lessonId: string
    }

  /* edit_plan events */
  | { type: "ADD_PRACTICE_TEST_SECTION"; targetSeconds?: number } // only ADD_MASTERY_LESSON_SECTION has custom length?
  | { type: "ADD_MASTERY_LESSON_SECTION"; targetSeconds?: number }
  | { type: "ADD_BOOT_CAMP_SECTION"; targetSeconds?: number } // only ADD_MASTERY_LESSON_SECTION has custom length?
  | { type: "ADD_BREAK_SECTION"; targetSeconds?: number } // only ADD_MASTERY_LESSON_SECTION has custom length?
  | { type: "PLAN_SETTINGS" }
  | {
      // transit to add lesson modal
      type: "ADD_LESSON_TO_SECTION"
      sectionId: string
    }
  | {
      type: "SECTION_SECOND_MODIFIER"
      sectionId: string
      targetSectionSeconds?: number
    }
  | { type: "SAVE_SECTION_SECOND" } // use targetSeconds from ctx.targetSectionSeconds
  | { type: "REMOVE_SECTION"; sectionId: string }
  | { type: "TOGGLE_EXPAND_SECTION"; sectionId: string }
  | { type: "EXPAND_ALL" }
  | { type: "COLLAPSE_ALL" }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "TOGGLE_BREAK_BEFORE_SECTION"; sectionId?: string }
  | { type: "TOGGLE_BREAK_AFTER_SECTION"; sectionId?: string }
  | {
      type: "TOGGLE_BREAK_BEFORE_LESSON"
      sectionId?: string
      lessonId?: string
    }
  | { type: "TOGGLE_BREAK_AFTER_LESSON"; sectionId?: string; lessonId?: string }
  | { type: "REORDER" }
  | { type: "OPEN_CONVERT_PLAN_CONFIRMATION" }
  | { type: "RESTORE_TO_RECOMMENDED" }
  | {
      type: "OPEN_VIEW_BREAK"
      sectionId?: string
      lessonId?: string
      breakType: BreakType
    }
  | { type: "CONVERT_TO_ADAPTIVE" }
  | { type: "CONVERT_TO_MANUAL" }
  | { type: "OPEN_FIRST_SAVING_CONFIRMATION" }
  | { type: "OPEN_CANCEL_CONFIRMATION" }
  /* edit_plan in multiple events */
  | {
      type: "PREVIEW"
      contentId?: string
      lessonId?: string
      planId?: string
      sectionId?: string
    } // either one.
  // contentId is preferred. If not provided, it'll fetch from evt.contentId or ctx.s
  // planId and SectionId are optional as well. If provided, there'll be more info
  | {
      type: "ADD_LESSON"
      itemContentId: string
      itemProperties?: {}
    } // or maybe just need the lesson id?
  | {
      // TODO: this one is not in statemachine???
      type: "UPDATE_LESSON"
      lessonId: string
      itemProperties?: {}
    }
  | {
      type: "REMOVE_LESSON"
      sectionId: string
      itemContentId: string
    }
  | {
      type: "REPLACE_LESSON"
      itemContentId: string
      itemProperties?: {}
    }

  /* edit_plan.add_lesson events */
  | { type: "FILTER" }
  | { type: "SORT" }
  /* edit_plan.lesson_detail_view */
  | { type: "DOWNLOAD_RESOURCES"; sectionId?: string; lessonId?: string }
  | { type: "TEACH_LIVE"; sectionId?: string; lessonId?: string }
  | { type: "MARK_COMPLETE"; sectionId?: string; lessonId?: string }
  | { type: "CHANGE_MICROSNAPS_COUNT"; lessonId: string; count: number }
  | { type: "BACK_TO_ADD_MASTERY_LESSON" }
  /* edit_plan.plan_settings events */
  | { type: "EDIT_HISTORY" }
  | { type: "TOGGLE_STUDY_PLAN_OUT_OF_ORDER" }
  | { type: "TOGGLE_RESET_MASTERY_LESSONS" }
  | { type: "SAVE_DRAFT_PLAN_SETTINGS"; planSettings: PlanSettings }
  | { type: "SAVE_GLOBAL_PLAN_SETTINGS"; globalPlanSettings: PlanSettings }
  | {
      type: "GET_SCORES_BY_STUDENT"
      searchText?: string
      currentPage?: number
      pageSize?: number
    }
  | { type: "DOWNLOAD_SCORES_BY_STUDENT" }
  /* edit_plan.reorder_mode */
  | { type: "DRAG_DROP_SECTION"; sectionId: string; index: number }
  | {
      type: "DRAG_DROP_ITEM"
      sectionId: string
      lessonId: string
      index: number
      toSectionId?: string
    }
  | { type: "DRAG_DROP_MOVE_OUT_SECTION"; sectionId: string; lessonId: string }
  | {
      type: "DRAG_DROP_MOVE_OUT_SECTION_CONFIRM"
    }
  | { type: "END_REORDER" }

  /* confirm_override */
  | { type: "no_override.update" }
  | { type: "override.update" }

  /* Event fired automatically on activation done */
  | { type: "done.invoke.activatePlan" }
  | { type: "done.invoke.buildPopulatedStudyPlan" }
  | { type: "done.invoke.buildStudyPlan" }
  | { type: "done.invoke.getRecommendedPlan" }
  | { type: "done.invoke.cancelPlan" }
  | { type: "done.invoke.getStudyPlansBySubject" }
  | { type: "done.invoke.getAdminRelevantPlanInfo" }
  | { type: "done.invoke.getAssignedSubjectsByRefScope" }
  | { type: "done.invoke.getGlobalSettings" }
  | { type: "done.invoke.getCurrentPlan" }
  | { type: "done.invoke.restoreToRecommendedPlan" }
  | { type: "done.invoke.getMaxAvailableContentDuration" }

  /* Study Plan V2 */
  | { type: "SELECT_SP_V2_SUBPAGE"; target: StudyPlanTargets }
  | { type: "UPDATE_REPORTING_FILTERS_STATE"; state: FiltersState }

export type Event = {
  type: string
  data: string
}

export enum BreakType {
  ABOVE = "ABOVE",
  BELOW = "BELOW"
}

export type StudyPlanSnackbar = {
  message: string
  withUndo?: boolean
  undoEvent?: Transitions
  error?: boolean
  variant?: string
}

export type Context = {
  initialUrlSubject?: string
  availableCourses: CourseInfo[]
  selfRefScope: RefScope
  currentPlanScope?: StudyPlanScope
  currentTargetPlanRefId?: string
  currentTarget: string
  previousParentState?: string // It might make more sense to have this set on exit instead of entry
  redirectUrl?: {
    path: string
    action: "push" | "replace"
  }
  loading: boolean
  /* landing_view */
  showLearnMore: boolean
  /* study_plans_overview context */
  program: Program
  subject: string
  isOldNav: boolean

  /* start_plan & build_plan */
  modifyHoursModal: boolean
  seconds?: number
  maxAvailableContentDuration?: number
  isBlank?: boolean
  /* review_plan & edit_plan context */
  // Data saved from API
  teacherAssignedSubjects: string[]
  relevantDistrictPlansInfo?: EntityPlanInfo[]
  relevantSchoolPlansInfo?: EntityPlanInfo[]
  relevantClassPlansInfo?: EntityInfo[]
  relevantStudentPlansInfo?: EntityInfo[]
  teacherPlanId?: string
  recommendedPlanId?: string
  recommendedPlanLevel?: string
  studyPlansListBySubjects: { [key: string]: EocClientStudyPlan }
  studyPlan?: EocClientDraftStudyPlan | null
  hasUnsavedStudyPlanChanges?: boolean
  publishedStudyPlan: EocClientStudyPlan
  recommendedStudyPlan: EocClientStudyPlan
  publishedPlanMeta?: EocClientStudyPlanMeta
  selectedPlanId?: string
  selectedSectionId?: string
  selectedLessonId?: string
  selectedBreakType?: BreakType
  classCountRelatedToSubject?: number
  /* teacher_view only context */
  showDefaultInheritance: boolean
  isStudentPlan?: boolean
  selectedEntity?: EntityInfo
  showMasterPlanInfo: boolean
  selectedForDownloadItem?: AvailableContentItem
  /* edit_plan only context */
  // Modal open/close
  assignClassModal: boolean
  addLessonModal: boolean
  modifySectionSecondsModal: boolean
  lessonDetailViewModal: boolean
  planSettingModal: boolean
  moveOutOfSectionModal: boolean
  confirmConvertingPlanModal: boolean
  viewBreakDrawer: boolean
  firstSavingConfirmModal: boolean
  confirmRecommendedRestore: boolean
  viewGlobalSettings: boolean
  confirmLeaveModal: boolean
  openConfirmResetMasteryLesson: boolean
  openConfirmRestoreMasteryLesson: boolean
  currentClassMasteryLessonAffectedCount: number | null
  pendingProgramChange?: {
    program: Program
    subject: string | null
  }
  pendingNavigationUrl?: string
  externalRedirectUrl?: {
    path: string
    action: "push" | "replace"
  }
  // Edit settings
  targetSectionSeconds?: number
  expanded: string[]
  expandAll: boolean
  isReorderMode: boolean
  planSettings: PlanSettings
  studyPlansOutOfOrderDefault?: boolean
  resetMasteryLessonsDefault?: boolean
  studentTestTimingValueDefault?: number
  allowPracticeTestPause?: boolean
  timingOptionDefault?: TimingOptionsValue
  customTimeMultiplier?: number
  // Students Scores Data
  scoresByStudentData?: {
    headers: ContentInfo[]
    students: StudentStudyPlanData[]
    pageCount: number
  }
  // Data saved from API
  editHistory?: string[]
  lessonOptions?: AvailableContentItem[]
  lessonDetails?: AvailableContentItem
  assignableClasses?: any[]
  /* confirm_override */
  existingPlanOptionsModal: boolean
  existingPlanOption?: ExistingPlanOptions
  overrideSchoolsCount?: number
  overrideTeachersCount?: number
  overrideClassesCount?: number
  snackbar?: StudyPlanSnackbar

  user: User

  //Study Plan V2
  studyPlanV2FeatureFlag?: boolean
  isStudyPlanV2Enabled?: boolean
  reportingFiltersState?: FiltersState
  progressByContentPlan?: EocClientStudyPlan
  progressBySubjectsModel?: Record<string, StudyPlanProgressModel>
}

export type SeviceErrors =
  | "getProgramCourses"
  | "getSectionAvailableItems"
  | "editPlanDuration"
  | "undo"
  | "redo"
  | "addSection"
  | "removeSection"
  | "updateSection"
  | "updateSectionSecond"
  | "addSectionItem"
  | "removeSectionItem"
  | "updateSectionItem"
  | "downloadResources"
  | "resolveStudentConflict"
  | "activatePlan"
  | "reorderSectionItem"
  | "reorderSection"
  | "newPlan"
  | "cancelPlan"
  | "moveItemToNewSection"
  | "getCurrentPlan"
  | "getCurrentPlanForEachSubjectByRefScope"
  | "getTeacherRelevantPlanInfo"
  | "getAdminRelevantPlanInfo"
  | "changeToAdaptivePlan"
  | "changeToManualPlan"
  | "getHistoryEdit"
  | "confirmOverride"
  | "reachedMaxHours"
  | "reachedMinHours"
  | "restoreRecommendedPlan"
  | "toggleBreakBeforeSection"
  | "toggleBreakBeforeLesson"
  | "toggleBreakAfterSection"
  | "toggleBreakAfterLesson"
  | "getEditHistory"
  | "assignSubjectToClasses"
  | "fetchClasses"
  | "changeMicroSnapsCount"
  | "fetchDraftStudyPlan"
  | "getGlobalSettings"
  | "getScoresByStudent"
  | "downloadScoresByStudent"
  | "getPlanAvailableItems"
  | "getRecommendedPlan"
  | "toggleItemTeachLive"
  | "getAssignedSubjectsByRefScope"
  | "getLessonPreview"
  | "getLessonDetails"
  | "replaceSectionItem"
  | "changeMicroSnapsCount"
  | "togglePublishedMarkComplete"
  | "toggleDraftMarkComplete"
  | "toggleStudyPlansOutOfOrder"
  | "toggleResetMasteryLessons"
  | "saveStudyPlanSettings"
  | "saveGlobalSettings"
  | "getSkillReportContentTargets"
  | "getSkillReportByContentId"
  | "getResetClassMasteryLessonsAffectedCount"
  | "getMaxAvailableContentDuration"
  | "getProgressDataByCurrentPlan"
  | "getProgressBySelectedStudyPlanSubject"
  | "getProgressByStudyPlanSubjects"
